import React, { useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import Snackbar from '@mui/joy/Snackbar';
import Alert from '@mui/joy/Alert';
import LinearProgress from '@mui/joy/LinearProgress';
import Tooltip from '@mui/joy/Tooltip';
import { v4 as uuidv4 } from 'uuid';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const UPLOAD_LAMBDA_URL = "https://id6eckqs6kz7fgg6euu7jptor40mqgul.lambda-url.us-east-2.on.aws/";
const S3_FOLDER = "inventory";

export default function ImageUpload({ images = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [showError, setShowError] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;

    // Validate files
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    for (const file of files) {
      if (file.size > maxFileSize) {
        setErrorMsg(`File ${file.name} is too large. Maximum size is 10MB.`);
        setShowError(true);
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        setErrorMsg(`File ${file.name} is not a supported image format.`);
        setShowError(true);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);
    setErrorMsg('');

    const urls = [...images];
    let successCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        const base64 = await readFileAsBase64(file);
        const uniqueName = `${uuidv4()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;

        const res = await fetch(UPLOAD_LAMBDA_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: uniqueName,
            type: file.type,
            content: base64,
            folder: S3_FOLDER,
          }),
        });

        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Upload failed: ${errorText}`);
        }
        
        const data = await res.json();
        if (!data.url) {
          throw new Error('No URL returned from upload service');
        }

        urls.push(data.url);
        successCount++;
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
        setErrorMsg(`Failed to upload ${file.name}: ${err.message}`);
        setShowError(true);
        // Continue with other files instead of stopping
      }
    }

    if (successCount > 0) {
      onChange(urls);
    }
    
    setUploading(false);
    setUploadProgress(0);
    setSelectedFiles([]);
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result); // includes `data:image/...;base64,...`
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  return (
    <Box>
      <Tooltip title="Upload images to add to your inventory item">
        <Button
          variant="outlined"
          component="label"
          disabled={uploading}
          startDecorator={uploading ? <CircularProgress size="sm" /> : <CloudUploadIcon />}
          sx={{
            borderColor: '#57C84D',
            color: '#57C84D',
            py: 1,
            px: 2,
            borderRadius: 'md',
            '&:hover': {
              borderColor: '#57C84D',
              bgcolor: 'rgba(87, 200, 77, 0.1)'
            },
            '&.Mui-disabled': {
              borderColor: '#57C84D',
              color: '#8BC34A'
            }
          }}
        >
          {uploading ? 'Uploading...' : 'Upload Images'}
          <input
            type="file"
            hidden
            multiple
            accept="image/*"
            onChange={handleFileSelection}
          />
        </Button>
      </Tooltip>

      {uploading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mr: 1 }}>
              {uploadProgress}%
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                determinate
                value={uploadProgress}
                sx={{
                  height: 6,
                  borderRadius: 'sm',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#57C84D'
                  }
                }}
              />
            </Box>
          </Box>
          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
            Uploading {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        color="danger"
      >
        <Alert
          onClose={() => setShowError(false)}
          color="danger"
          variant="solid"
          sx={{ width: '100%' }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
